import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, Loader2, RefreshCw, Search, FileText } from 'lucide-react';
import AccessTokenManager from '../services/accessTokenManager';
import { bidService } from '../services/bidService';
import { BidRegistryItem, UserMetrics } from '../models/bid';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (minutes < 60) return rem ? `${minutes}m ${rem}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remM = minutes % 60;
  return remM ? `${hours}h ${remM}m` : `${hours}h`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatMoney = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();

  // --- Reprocess card state ---
  const [bidId, setBidId] = useState('');
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMessage, setReprocessMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- User lookup card state ---
  const [lookupUserId, setLookupUserId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<UserMetrics | null>(null);
  const [bids, setBids] = useState<BidRegistryItem[] | null>(null);

  const handleResubmit = async () => {
    if (!bidId.trim()) {
      setReprocessMessage({ type: 'error', text: 'Please enter a Bid ID' });
      return;
    }

    setReprocessing(true);
    setReprocessMessage(null);

    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids/${bidId.trim()}/reprocess`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to reprocess bid' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      setReprocessMessage({ type: 'success', text: result.message || 'Bid reprocessing initiated successfully' });
      setBidId('');
    } catch (error) {
      console.error('Error reprocessing bid:', error);
      setReprocessMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to reprocess bid'
      });
    } finally {
      setReprocessing(false);
    }
  };

  const handleLookup = async () => {
    const target = lookupUserId.trim();
    if (!target) {
      setLookupError('Please enter a User ID');
      return;
    }

    setLookupLoading(true);
    setLookupError(null);
    setMetrics(null);
    setBids(null);

    try {
      const [metricsRes, registryRes] = await Promise.all([
        bidService.getUserMetrics(target),
        bidService.getUserBidRegistry(target),
      ]);
      setMetrics(metricsRes);
      setBids(registryRes.bidRegistry);
    } catch (error) {
      console.error('Error looking up user:', error);
      setLookupError(error instanceof Error ? error.message : 'Failed to look up user');
    } finally {
      setLookupLoading(false);
    }
  };

  const sortedBids = bids
    ? [...bids].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    : null;


  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-6xl">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Administrative tools and controls</p>
      </div>

      {/* Reprocess Bid Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
            Reprocess Bid
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs sm:text-sm text-gray-600">
            Enter a Bid ID to reprocess the bid extraction and analysis.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={bidId}
              onChange={(e) => setBidId(e.target.value)}
              placeholder="Enter Bid ID (UUID)"
              className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={reprocessing}
            />
            <Button
              onClick={handleResubmit}
              disabled={reprocessing || !bidId.trim()}
              className="gap-2 w-full sm:w-auto whitespace-nowrap"
            >
              {reprocessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Processing...</span>
                  <span className="sm:hidden">Processing</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Reprocess
                </>
              )}
            </Button>
          </div>

          {reprocessMessage && (
            <div
              className={`flex items-start gap-2 p-3 rounded-md ${
                reprocessMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {reprocessMessage.type === 'error' && <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />}
              <p className="text-xs sm:text-sm break-words">{reprocessMessage.text}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Lookup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            User Lookup — Bids &amp; Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs sm:text-sm text-gray-600">
            Enter a User ID (Auth0 sub, e.g. <code className="text-xs bg-gray-100 px-1 rounded">auth0|abc123</code>) to view their bids and usage metrics.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={lookupUserId}
              onChange={(e) => setLookupUserId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
              placeholder="Enter User ID"
              className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={lookupLoading}
            />
            <Button
              onClick={handleLookup}
              disabled={lookupLoading || !lookupUserId.trim()}
              className="gap-2 w-full sm:w-auto whitespace-nowrap"
            >
              {lookupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Look up
                </>
              )}
            </Button>
          </div>

          {lookupError && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-800 border border-red-200">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm break-words">{lookupError}</p>
            </div>
          )}

          {/* Metrics summary */}
          {metrics && (
            <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Usage metrics</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Total bids</div>
                  <div className="font-semibold text-gray-900">{metrics.bidCount}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">First bid</div>
                  <div className="font-semibold text-gray-900">{metrics.firstBidAt ? new Date(metrics.firstBidAt).toLocaleDateString() : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Last bid</div>
                  <div className="font-semibold text-gray-900">{metrics.lastBidAt ? new Date(metrics.lastBidAt).toLocaleDateString() : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Timing samples</div>
                  <div className="font-semibold text-gray-900">{metrics.processingMs.count} / {metrics.bidCount}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Avg run time</div>
                  <div className="font-semibold text-gray-900">{formatDuration(metrics.processingMs.avgMs)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">p50 run time</div>
                  <div className="font-semibold text-gray-900">{formatDuration(metrics.processingMs.p50Ms)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">p95 run time</div>
                  <div className="font-semibold text-gray-900">{formatDuration(metrics.processingMs.p95Ms)}</div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="text-xs text-gray-900 leading-tight">
                    {Object.entries(metrics.statusHistogram).length === 0
                      ? '—'
                      : Object.entries(metrics.statusHistogram).map(([k, v]) => (
                          <div key={k}><span className="font-semibold">{v}</span> {k}</div>
                        ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bids table */}
          {sortedBids && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-700">Bids ({sortedBids.length})</span>
              </div>
              {sortedBids.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">No bids for this user.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Contractor</th>
                        <th className="px-3 py-2 text-left font-medium">Project</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                        <th className="px-3 py-2 text-right font-medium">Total</th>
                        <th className="px-3 py-2 text-right font-medium">Run time</th>
                        <th className="px-3 py-2 text-left font-medium">Assessment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedBids.map((b) => (
                        <tr
                          key={b.id}
                          onClick={() => navigate(`/bids/${b.id}`)}
                          className="border-b border-gray-100 last:border-0 hover:bg-blue-50 cursor-pointer"
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{formatDate(b.date)}</td>
                          <td className="px-3 py-2 text-gray-900">{b.contractorName || '—'}</td>
                          <td className="px-3 py-2 text-gray-900 max-w-xs truncate">{b.projectAddress || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{b.status}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-gray-900">{formatMoney(b.totalAmount)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-gray-900">{formatDuration(b.processingDurationMs)}</td>
                          <td className="px-3 py-2 text-gray-900 max-w-xs truncate">{b.overallAssessment || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
